import React from 'react';

import type { Text } from '@zillow/constellation';
import { FormField, Input, Label, Listbox, Spacer, Textarea } from '@zillow/constellation';

import type { Section } from '../../../models/index';
import { PESTICIDES } from '../../../constants';
import type { HTMLElementEvent } from '../../../models/Types';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function Pesticides({ section, setSectionValues }: Props) {
  function onPestControlledChange(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PESTICIDES.FIELD_NAME.PEST_CONTROLLED]: value,
    });
  }

  function onNameAndBrandChange(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PESTICIDES.FIELD_NAME.NAME_AND_BRAND]: value,
    });
  }

  function onPesticidesLocationChange(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PESTICIDES.FIELD_NAME.PESTICIDES_LOCATION]: value,
    });
  }

  function onScheduleChange(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PESTICIDES.FIELD_NAME.SCHEDULE]: value,
    });
  }

  function onOptionChange(value: string): void {
    setSectionValues({
      [PESTICIDES.FIELD_NAME.NO_PESTICIDES]: value === PESTICIDES.FIELD_NAME.NO_PESTICIDES,
      [PESTICIDES.FIELD_NAME.REGISTERED_PEST_CONTROL_COMPANY]:
        value === PESTICIDES.FIELD_NAME.REGISTERED_PEST_CONTROL_COMPANY,
      [PESTICIDES.FIELD_NAME.LANDLORD_LOCATION]: value === PESTICIDES.FIELD_NAME.LANDLORD_LOCATION,
    });
  }

  const landlordLocationSelected = section.getFieldValue(PESTICIDES.FIELD_NAME.LANDLORD_LOCATION);

  return (
    <>
      <FormField
        label={<Label size="lg">Will there be routine pesticide use at the property?</Label>}
        control={
          <Listbox
            size="fluid"
            onChange={onOptionChange}
            appearance="radio"
            value={
              section.getFieldValue(PESTICIDES.FIELD_NAME.NO_PESTICIDES)
                ? PESTICIDES.FIELD_NAME.NO_PESTICIDES
                : section.getFieldValue(PESTICIDES.FIELD_NAME.REGISTERED_PEST_CONTROL_COMPANY)
                  ? PESTICIDES.FIELD_NAME.REGISTERED_PEST_CONTROL_COMPANY
                  : section.getFieldValue(PESTICIDES.FIELD_NAME.LANDLORD_LOCATION)
                    ? PESTICIDES.FIELD_NAME.LANDLORD_LOCATION
                    : ''
            }
            options={[
              {
                value: PESTICIDES.FIELD_NAME.NO_PESTICIDES,
                label: 'No pesticide use',
                disabled: section.isFieldDisabled(PESTICIDES.FIELD_NAME.NO_PESTICIDES),
              },
              {
                value: PESTICIDES.FIELD_NAME.REGISTERED_PEST_CONTROL_COMPANY,
                label: 'Yes, by a registered pest control company',
                meta: 'You must provide the pest control company‘s notice regarding pest(s) controlled, the pesticides used, and a warning regarding toxic chemicals.',
                disabled: section.isFieldDisabled(PESTICIDES.FIELD_NAME.REGISTERED_PEST_CONTROL_COMPANY),
              },
              {
                value: PESTICIDES.FIELD_NAME.LANDLORD_LOCATION,
                label: 'Yes, by landlord or other authorized agent',
                disabled: section.isFieldDisabled(PESTICIDES.FIELD_NAME.LANDLORD_LOCATION),
              },
            ]}
          />
        }
      />
      {landlordLocationSelected && (
        <Spacer marginTop="sm">
          <FormField
            label={<Label>Name of pest or pests to be controlled</Label>}
            control={
              <Input
                aria-labelledby="controlled-pest-name"
                value={section.getFieldValue(PESTICIDES.FIELD_NAME.PEST_CONTROLLED)}
                onChange={onPestControlledChange}
                placeholder="(example: Cockroaches, moths, and ants)"
                marginBottom="sm"
              />
            }
          />
          <FormField
            label={<Label>Name and brand of pesticide used</Label>}
            control={
              <Input
                aria-labelledby="name-and-brand"
                value={section.getFieldValue(PESTICIDES.FIELD_NAME.NAME_AND_BRAND)}
                onChange={onNameAndBrandChange}
                marginBottom="sm"
              />
            }
          />
          <FormField
            label={<Label>Where will you apply pesticides?</Label>}
            control={
              <Input
                aria-labelledby="pesticide-location"
                value={section.getFieldValue(PESTICIDES.FIELD_NAME.PESTICIDES_LOCATION)}
                onChange={onPesticidesLocationChange}
                marginBottom="sm"
              />
            }
          />
          <FormField
            label={<Label>Schedule for routine pesticide application</Label>}
            control={
              <Textarea
                aria-labelledby="pesticide-schedule"
                value={section.getFieldValue(PESTICIDES.FIELD_NAME.SCHEDULE)}
                onChange={onScheduleChange}
              />
            }
          />
        </Spacer>
      )}
    </>
  );
}

Pesticides.description = 'Asks Irene whether or not there is pesticide use on the property';

export default Pesticides;
